import type { PassageUserType } from "@/src/shared/contracts/app";
import {
  getBaseProvider,
  getProviderConnectionState,
} from "@/src/features/integrations/providerAccounts";

/**
 * Cross-window provider connection hints.
 *
 * A provider connect updates `sessionTokens` only in the renderer that ran it.
 * Other windows and tabs of the same account learn about it through a hint on
 * the shared BroadcastChannel. The hint carries identity and a provider name,
 * never tokens, and it is never proof: each receiver re-reads its own session
 * from the authenticated server before changing anything.
 */
export const PROVIDER_CONNECTION_SYNC_MESSAGE_TYPE = "PROVIDER_CONNECTION_REVERIFY";

export type ProviderConnectionSyncMessage = {
  type: typeof PROVIDER_CONNECTION_SYNC_MESSAGE_TYPE;
  provider: string;
  userId: string;
  companyId: string;
  timestamp: number;
};

type SessionTokens = PassageUserType["sessionTokens"];
type SessionIdentitySlice = Pick<PassageUserType, "isAuthorized" | "userId" | "companyId">;

/** The `records?query=getUserInfo` row, the same source the initial hydration reads. */
export type AuthoritativeUserDetails = {
  userId?: unknown;
  companyId?: unknown;
  sessionTokens?: unknown;
};

const PROVIDER_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,63}$/;

function sessionIdentity(session: SessionIdentitySlice): { userId: string; companyId: string } | null {
  const userId = String(session.userId || "").trim();
  const companyId = String(session.companyId || "").trim();
  if (session.isAuthorized !== true || !userId || userId === "demo" || !companyId) return null;
  return { userId, companyId };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function createProviderConnectionSyncMessage(
  session: SessionIdentitySlice,
  provider: string,
): ProviderConnectionSyncMessage | null {
  const identity = sessionIdentity(session);
  const baseProvider = getBaseProvider(provider);
  if (!identity || !PROVIDER_PATTERN.test(baseProvider)) return null;
  return {
    type: PROVIDER_CONNECTION_SYNC_MESSAGE_TYPE,
    provider: baseProvider,
    ...identity,
    timestamp: Date.now(),
  };
}

export function isProviderConnectionSyncForSession(
  value: unknown,
  session: SessionIdentitySlice,
): value is ProviderConnectionSyncMessage {
  if (!isRecord(value)) return false;
  const identity = sessionIdentity(session);
  return value.type === PROVIDER_CONNECTION_SYNC_MESSAGE_TYPE
    && typeof value.provider === "string"
    && PROVIDER_PATTERN.test(value.provider)
    && identity !== null
    && value.userId === identity.userId
    && value.companyId === identity.companyId;
}

/** Base providers whose connection state changed to connected between two token maps. */
export function newlyConnectedProviders(
  previous: SessionTokens | null | undefined,
  next: SessionTokens | null | undefined,
): string[] {
  if (previous === next || !next) return [];
  const providers = new Set(Object.keys(next).map(getBaseProvider).filter(Boolean));
  return [...providers].filter((provider) =>
    getProviderConnectionState(next, provider) === "connected"
    && getProviderConnectionState(previous, provider) !== "connected");
}

/**
 * Applies the server's view of one provider to the local session. Returns null
 * unless the server row belongs to the same account and shows the provider
 * connected; the hint alone never changes local state.
 */
export function mergeReverifiedProviderConnection(
  session: PassageUserType,
  provider: string,
  details: AuthoritativeUserDetails | null | undefined,
): PassageUserType | null {
  const identity = sessionIdentity(session);
  if (!identity || !isRecord(details)) return null;
  if (String(details.userId || "").trim() !== identity.userId) return null;
  const rowCompanyId = String(details.companyId || "").trim();
  if (rowCompanyId && rowCompanyId !== identity.companyId) return null;
  if (!isRecord(details.sessionTokens)) return null;

  const baseProvider = getBaseProvider(provider);
  const serverTokens = details.sessionTokens as SessionTokens;
  if (getProviderConnectionState(serverTokens, baseProvider) !== "connected") return null;

  const sessionTokens: SessionTokens = {};
  for (const [vendorKey, token] of Object.entries(session.sessionTokens || {})) {
    if (getBaseProvider(vendorKey) !== baseProvider) sessionTokens[vendorKey] = token;
  }
  for (const [vendorKey, token] of Object.entries(serverTokens)) {
    if (getBaseProvider(vendorKey) === baseProvider && isRecord(token)) sessionTokens[vendorKey] = token;
  }
  return { ...session, sessionTokens };
}

/** The server write that backs a connect can land just after the hint. */
export const PROVIDER_REVERIFY_DELAYS_MS: readonly number[] = [0, 1_500, 5_000];

export type ProviderReverifyDependencies = {
  /** The current session, or null while this window is signing out. */
  getSession: () => PassageUserType | null;
  fetchUserDetails: () => Promise<AuthoritativeUserDetails | null | undefined>;
  applySession: (session: PassageUserType) => void;
  sleep?: (ms: number) => Promise<void>;
  delaysMs?: readonly number[];
};

export type ProviderReverifyOutcome = "applied" | "already_connected" | "identity_changed" | "not_confirmed";

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** This base provider's token entries, keyed by vendor key. */
function providerTokenEntries(tokens: SessionTokens | null | undefined, baseProvider: string): Map<string, unknown> {
  const entries = new Map<string, unknown>();
  for (const [vendorKey, token] of Object.entries(tokens || {})) {
    if (getBaseProvider(vendorKey) === baseProvider) entries.set(vendorKey, token);
  }
  return entries;
}

function sameProviderTokenEntries(left: Map<string, unknown>, right: Map<string, unknown>): boolean {
  if (left.size !== right.size) return false;
  for (const [vendorKey, token] of left) {
    if (!right.has(vendorKey) || right.get(vendorKey) !== token) return false;
  }
  return true;
}

/**
 * Re-reads the session from the server until it confirms the hinted provider,
 * or gives up after the last delay. The account is checked before every fetch
 * and again before applying, so a sign-out or account switch in this window
 * while a request is in flight discards the result. The provider's local token
 * entries are snapshotted when each read starts; if they change before the
 * response arrives (a disconnect or a token refresh here), the response may
 * predate that change, so it is discarded and the next attempt reads again.
 */
export async function reverifyProviderConnection(
  message: ProviderConnectionSyncMessage,
  deps: ProviderReverifyDependencies,
): Promise<ProviderReverifyOutcome> {
  const sleep = deps.sleep ?? defaultSleep;
  for (const delayMs of deps.delaysMs ?? PROVIDER_REVERIFY_DELAYS_MS) {
    if (delayMs > 0) await sleep(delayMs);
    const before = deps.getSession();
    if (!before || !isProviderConnectionSyncForSession(message, before)) return "identity_changed";
    if (getProviderConnectionState(before.sessionTokens, message.provider) === "connected") {
      return "already_connected";
    }
    const entriesAtRead = providerTokenEntries(before.sessionTokens, message.provider);

    let details: AuthoritativeUserDetails | null | undefined;
    try {
      details = await deps.fetchUserDetails();
    } catch (error) {
      console.warn("[Integrations] Cross-window connection check failed", error);
      continue;
    }

    const latest = deps.getSession();
    if (!latest || !isProviderConnectionSyncForSession(message, latest)) return "identity_changed";
    if (getProviderConnectionState(latest.sessionTokens, message.provider) === "connected") {
      return "already_connected";
    }
    if (!sameProviderTokenEntries(entriesAtRead, providerTokenEntries(latest.sessionTokens, message.provider))) {
      continue;
    }
    const next = mergeReverifiedProviderConnection(latest, message.provider, details);
    if (next) {
      deps.applySession(next);
      return "applied";
    }
  }
  return "not_confirmed";
}

export type ProviderConnectionSyncBridgeDependencies = Omit<ProviderReverifyDependencies, "applySession"> & {
  postMessage: (message: ProviderConnectionSyncMessage) => void;
  setSession: (session: PassageUserType) => void;
};

export type ProviderConnectionSyncBridge = {
  /** Call on every session change in this window; null while signing out. */
  handleSessionChange: (next: PassageUserType | null, previous: PassageUserType | null) => void;
  /** Call with every message from the shared channel. */
  handleMessage: (value: unknown) => Promise<ProviderReverifyOutcome | "ignored" | "in_flight">;
};

/**
 * One renderer's side of the cross-window connect hint: it announces
 * connections made here and refreshes from the server on hints from elsewhere.
 * A window that is closed has no bridge, so its hint goes unheard and its wait
 * simply expires.
 */
export function createProviderConnectionSyncBridge(
  deps: ProviderConnectionSyncBridgeDependencies,
): ProviderConnectionSyncBridge {
  const inFlight = new Set<string>();
  let applyingReverifiedSession = false;

  return {
    handleSessionChange(next, previous) {
      if (applyingReverifiedSession || !next || !previous) return;
      if (next.sessionTokens === previous.sessionTokens) return;
      const identity = sessionIdentity(next);
      const previousIdentity = sessionIdentity(previous);
      // A login, logout, or account switch is not a connect in this account.
      if (!identity || !previousIdentity || identity.userId !== previousIdentity.userId
        || identity.companyId !== previousIdentity.companyId) return;
      for (const provider of newlyConnectedProviders(previous.sessionTokens, next.sessionTokens)) {
        const message = createProviderConnectionSyncMessage(next, provider);
        if (message) deps.postMessage(message);
      }
    },
    async handleMessage(value) {
      const session = deps.getSession();
      if (!session || !isProviderConnectionSyncForSession(value, session)) return "ignored";
      const requestKey = `${value.userId}:${value.companyId}:${value.provider}`;
      if (inFlight.has(requestKey)) return "in_flight";
      inFlight.add(requestKey);
      try {
        return await reverifyProviderConnection(value, {
          ...deps,
          applySession: (nextSession) => {
            // The refreshed window must not echo the hint back to the sender.
            applyingReverifiedSession = true;
            try {
              deps.setSession(nextSession);
            } finally {
              applyingReverifiedSession = false;
            }
          },
        });
      } finally {
        inFlight.delete(requestKey);
      }
    },
  };
}
